import { useShallow } from 'zustand/react/shallow';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Permissions as P,
  RelationshipType as R,
  canActOnMember,
  parsePermissions,
  type Channel,
  type Device,
  type GuildFull,
  type VoiceState,
} from '@comms/shared';
import { fromBase64, safetyNumber } from '@comms/crypto';
import { displayName, useStore } from '../../store';
import { api } from '../../lib/api';
import { has, usePermissions } from '../../hooks/permissions';
import { Avatar, Button, ErrorText, Input, Modal, Spinner, Tabs } from '../ui';

export function CreateGuildModal({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<'create' | 'join'>('create');
  const [name, setName] = useState(`${useStore.getState().me?.displayName}'s server`);
  const [link, setLink] = useState('');
  const [error, setError] = useState<unknown>(null);
  const navigate = useNavigate();
  async function submit() {
    try {
      if (tab === 'create') {
        const g = await api<GuildFull>('POST', '/guilds', { name });
        onClose();
        navigate(`/channels/${g.id}`);
      } else {
        const code = link.trim().split('/').pop()!;
        const res = await api<{ guildId: string }>('POST', `/invites/${code}`);
        onClose();
        navigate(`/channels/${res.guildId}`);
      }
    } catch (err) {
      setError(err);
    }
  }
  return (
    <Modal title="Add a server" onClose={onClose}>
      <Tabs
        tabs={[
          { id: 'create', label: 'Create' },
          { id: 'join', label: 'Join' },
        ]}
        value={tab}
        onChange={setTab}
      />
      <div className="mt-4 space-y-4">
        {tab === 'create' ? (
          <Input label="Server name" value={name} onChange={(e) => setName(e.target.value)} />
        ) : (
          <Input
            label="Invite link or code"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://…/invite/abc123"
          />
        )}
        <ErrorText error={error} />
        <Button
          className="w-full"
          onClick={submit}
          disabled={tab === 'create' ? name.trim().length < 2 : !link.trim()}
        >
          {tab === 'create' ? 'Create server' : 'Join server'}
        </Button>
      </div>
    </Modal>
  );
}

export function ProfileModal({
  userId,
  guildId,
  onClose,
}: {
  userId: string;
  guildId?: string | null;
  onClose: () => void;
}) {
  const user = useStore((s) => s.users[userId]);
  const me = useStore((s) => s.me!);
  const rel = useStore((s) => s.relationships[userId]);
  const member = useStore((s) => (guildId ? s.members[guildId]?.[userId] : undefined));
  const myMember = useStore((s) => (guildId ? s.members[guildId]?.[s.me!.id] : undefined));
  const guild = useStore((s) => (guildId ? s.guilds[guildId] : undefined));
  const presence = useStore((s) => s.presences[userId]);
  const perms = usePermissions(guildId);
  const voiceState = useStore((s) =>
    guildId && s.voiceStates[userId]?.guildId === guildId ? s.voiceStates[userId] : undefined,
  );
  const [error, setError] = useState<unknown>(null);
  const navigate = useNavigate();
  if (!user) return null;
  const self = userId === me.id;
  const outranks =
    !!guild &&
    !!myMember &&
    canActOnMember(
      {
        ownerId: guild.ownerId,
        roles: guild.roles.map((r) => ({
          id: r.id,
          permissions: parsePermissions(r.permissions),
          position: r.position,
        })),
      },
      { userId: me.id, roleIds: myMember.roles },
      { userId, roleIds: member?.roles ?? [] },
    );

  async function message() {
    try {
      const c = await api<Channel>('POST', '/users/@me/channels', { recipientIds: [userId] });
      onClose();
      navigate(`/channels/@me/${c.id}`);
    } catch (err) {
      setError(err);
    }
  }
  async function timeout(minutes: number | null) {
    await api('PATCH', `/guilds/${guildId}/members/${userId}`, {
      communicationDisabledUntil: minutes
        ? new Date(Date.now() + minutes * 60_000).toISOString()
        : null,
    }).catch(setError);
  }

  return (
    <Modal onClose={onClose}>
      <div className="flex items-center gap-4">
        <Avatar userId={userId} size={80} showStatus />
        <div>
          <h2 className="text-xl font-bold text-text-0">{member?.nickname ?? user.displayName}</h2>
          <p className="text-sm text-text-3">{user.username}</p>
          {presence?.customStatus && <p className="text-sm text-text-2">{presence.customStatus}</p>}
        </div>
      </div>
      {user.bio && <p className="mt-4 whitespace-pre-wrap text-sm text-text-1">{user.bio}</p>}
      {member && guild && (
        <div className="mt-4">
          <h3 className="mb-1 text-xs font-bold uppercase text-text-3">Roles</h3>
          <div className="flex flex-wrap gap-1">
            {guild.roles
              .filter((r) => member.roles.includes(r.id))
              .map((r) => (
                <span key={r.id} className="rounded bg-bg-1 px-2 py-0.5 text-xs text-text-1">
                  {r.name}
                </span>
              ))}
            {member.roles.length === 0 && <span className="text-xs text-text-3">No roles</span>}
          </div>
          {member.communicationDisabledUntil &&
            new Date(member.communicationDisabledUntil) > new Date() && (
              <p className="mt-2 text-xs text-warning">
                Timed out until {new Date(member.communicationDisabledUntil).toLocaleString()}
              </p>
            )}
        </div>
      )}
      {!self && (
        <div className="mt-6 flex flex-wrap gap-2">
          <Button onClick={message}>Message</Button>
          {!rel && (
            <Button
              variant="secondary"
              onClick={() =>
                void api('PUT', `/users/@me/relationships/${userId}`, {}).catch(setError)
              }
            >
              Add friend
            </Button>
          )}
          {rel?.type === R.INCOMING && (
            <Button
              variant="secondary"
              onClick={() =>
                void api('PUT', `/users/@me/relationships/${userId}`, {}).catch(setError)
              }
            >
              Accept request
            </Button>
          )}
          {rel?.type !== R.BLOCKED ? (
            <Button
              variant="ghost"
              onClick={() =>
                confirm(`Block ${user.displayName}?`) &&
                api('PUT', `/users/@me/relationships/${userId}`, { type: 2 }).catch(setError)
              }
            >
              Block
            </Button>
          ) : (
            <Button
              variant="ghost"
              onClick={() => void api('DELETE', `/users/@me/relationships/${userId}`)}
            >
              Unblock
            </Button>
          )}
          {guildId &&
            voiceState &&
            (has(perms, P.MUTE_MEMBERS) ||
              has(perms, P.DEAFEN_MEMBERS) ||
              has(perms, P.MOVE_MEMBERS)) && (
              <VoiceModeration
                guildId={guildId}
                userId={userId}
                state={voiceState}
                perms={perms}
                onError={setError}
              />
            )}
          {guildId && outranks && has(perms, P.MODERATE_MEMBERS) && (
            <select
              className="rounded bg-bg-0 p-2 text-sm text-text-1"
              value=""
              onChange={(e) =>
                e.target.value &&
                void timeout(e.target.value === 'clear' ? null : Number(e.target.value))
              }
            >
              <option value="">Timeout…</option>
              <option value={5}>5 minutes</option>
              <option value={60}>1 hour</option>
              <option value={1440}>1 day</option>
              <option value={10080}>1 week</option>
              <option value="clear">Remove timeout</option>
            </select>
          )}
        </div>
      )}
      <ErrorText error={error} />
    </Modal>
  );
}

export function SafetyModal({ channelId, onClose }: { channelId: string; onClose: () => void }) {
  const channel = useStore((s) => s.channels[channelId]);
  const me = useStore((s) => s.me!);
  const [rows, setRows] = useState<{ userId: string; devices: Device[]; number: string }[] | null>(
    null,
  );
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    (async () => {
      const mine = await api<Device[]>(`GET`, `/users/${me.id}/devices`);
      const others = (channel?.recipients ?? []).filter((r) => r.id !== me.id);
      const out = [];
      for (const o of others) {
        const devices = await api<Device[]>('GET', `/users/${o.id}/devices`);
        const number = await safetyNumber(
          { userId: me.id, deviceKeys: mine.map((d) => fromBase64(d.identityKey)) },
          { userId: o.id, deviceKeys: devices.map((d) => fromBase64(d.identityKey)) },
        );
        out.push({ userId: o.id, devices, number });
      }
      setRows(out);
    })().catch(setError);
  }, [channel, me.id]);
  return (
    <Modal title="Verify encryption" onClose={onClose} wide>
      <p className="mb-4 text-sm text-text-2">
        Compare the safety number with each person in person or over a call. If both of you see the
        same number, nobody (not even this server) has added a device to eavesdrop. The number
        changes when anyone adds or removes a device.
      </p>
      {!rows && !error && <Spinner />}
      {rows?.map((r) => (
        <div key={r.userId} className="mb-4 rounded bg-bg-1 p-4">
          <div className="mb-2 flex items-center gap-2">
            <Avatar userId={r.userId} size={28} />
            <span className="font-semibold text-text-0">
              {displayName(useStore.getState(), r.userId)}
            </span>
            <span className="text-xs text-text-3">{r.devices.length} device(s)</span>
          </div>
          <code className="block font-mono text-lg tracking-wider text-text-0">{r.number}</code>
          <ul className="mt-2 text-xs text-text-3">
            {r.devices.map((d) => (
              <li key={d.id}>
                {d.name} — added {new Date(d.createdAt).toLocaleDateString()}
              </li>
            ))}
          </ul>
        </div>
      ))}
      <ErrorText error={error} />
    </Modal>
  );
}

function VoiceModeration({
  guildId,
  userId,
  state,
  perms,
  onError,
}: {
  guildId: string;
  userId: string;
  state: VoiceState;
  perms: bigint;
  onError: (e: unknown) => void;
}) {
  const voiceChannels = useStore(
    useShallow((s) =>
      Object.values(s.channels).filter(
        (c) => c.guildId === guildId && c.type === 2 && c.id !== state.channelId,
      ),
    ),
  );
  const act = (body: Record<string, unknown>) =>
    void api('PATCH', `/guilds/${guildId}/voice/${userId}`, body).catch(onError);
  return (
    <div className="flex w-full flex-wrap items-center gap-2 rounded bg-bg-1 p-2 text-sm">
      <span className="text-text-3">Voice:</span>
      {has(perms, P.MUTE_MEMBERS) && (
        <Button
          variant={state.serverMute ? 'danger' : 'secondary'}
          className="py-1"
          onClick={() => act({ serverMute: !state.serverMute })}
        >
          {state.serverMute ? 'Server unmute' : 'Server mute'}
        </Button>
      )}
      {has(perms, P.DEAFEN_MEMBERS) && (
        <Button
          variant={state.serverDeaf ? 'danger' : 'secondary'}
          className="py-1"
          onClick={() => act({ serverDeaf: !state.serverDeaf })}
        >
          {state.serverDeaf ? 'Server undeafen' : 'Server deafen'}
        </Button>
      )}
      {has(perms, P.MOVE_MEMBERS) && (
        <>
          <select
            className="rounded bg-bg-0 p-1.5 text-text-1"
            value=""
            onChange={(e) => e.target.value && act({ channelId: e.target.value })}
          >
            <option value="">Move to…</option>
            {voiceChannels.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <Button
            variant="ghost"
            className="py-1 text-danger"
            onClick={() => act({ channelId: null })}
          >
            Disconnect
          </Button>
        </>
      )}
    </div>
  );
}
